const RECOMMENDATION_STYLES = {
  PROCEED: "bg-[#2F3E2F] text-white",
  PROCEED_WITH_CAUTION: "bg-[#D98032] text-white",
  SECOND_INTERVIEW: "bg-[#A64D2E] text-white",
  DECLINE: "bg-[#5E5C58] text-white",
};

export default function AnalysisView({ analysis }) {
  if (!analysis) return null;

  if (analysis.error && !analysis.headline) {
    return (
      <div className="mt-12 p-8 rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)]" data-testid="analysis-error">
        <div className="text-sm uppercase tracking-[0.18em] text-[var(--tt-muted)] mb-2">Analysis</div>
        <p className="text-[var(--tt-text)]">
          We received your submission, but the AI analysis could not be parsed.
        </p>
        {analysis.raw && (
          <pre className="mt-4 text-xs bg-[var(--tt-surface-2)] p-4 rounded overflow-auto whitespace-pre-wrap">
            {analysis.raw}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div className="mt-12 space-y-8" data-testid="analysis-view">
      {/* Headline card */}
      <div className="rounded-2xl bg-[var(--tt-secondary)] text-white p-8 sm:p-10">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-white/60 mb-3">
              AI Analysis · Claude Sonnet 4.5
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-snug max-w-2xl">
              {analysis.headline}
            </h2>
            {analysis.summary && (
              <p className="mt-4 text-white/80 leading-relaxed max-w-2xl">
                {analysis.summary}
              </p>
            )}
          </div>
          <div className="flex flex-col items-start sm:items-end gap-3 shrink-0">
            {typeof analysis.overall_fit_score === "number" && (
              <div className="text-right">
                <div className="font-display text-5xl font-bold leading-none">
                  {analysis.overall_fit_score}
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-white/60 mt-1">
                  Overall Fit
                </div>
              </div>
            )}
            {analysis.recommendation && (
              <span
                data-testid="recommendation-badge"
                className={`text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${
                  RECOMMENDATION_STYLES[analysis.recommendation] || "bg-white/10 text-white"
                }`}
              >
                {String(analysis.recommendation).replace(/_/g, " ")}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Strengths / Concerns */}
      <div className="grid md:grid-cols-2 gap-6">
        <ListCard title="Strengths" items={analysis.strengths} variant="positive" testid="strengths-card" />
        <ListCard title="Concerns" items={analysis.concerns} variant="warning" testid="concerns-card" />
      </div>

      {/* Cultural signals */}
      {analysis.cultural_signals && (
        <div className="rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] p-8" data-testid="cultural-signals">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--tt-primary)] font-semibold mb-4">
            Cultural Signals
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <CulturalCard label="Pakikisama" subtitle="Group harmony" body={analysis.cultural_signals.pakikisama} />
            <CulturalCard label="Hiya" subtitle="Reputation & modesty" body={analysis.cultural_signals.hiya} />
            <CulturalCard label="Utang na loob" subtitle="Loyalty & reciprocity" body={analysis.cultural_signals.utang_na_loob} />
          </div>
        </div>
      )}

      {/* Self-awareness */}
      {analysis.self_awareness_read && (
        <div className="rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] p-8">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--tt-primary)] font-semibold mb-3">
            Self-Awareness Read
          </div>
          <p className="text-[var(--tt-text)] leading-relaxed">{analysis.self_awareness_read}</p>
        </div>
      )}

      {/* Part breakdown */}
      {Array.isArray(analysis.part_breakdown) && analysis.part_breakdown.length > 0 && (
        <div className="rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] p-8">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--tt-primary)] font-semibold mb-4">
            Part-by-Part Read
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {analysis.part_breakdown.map((p, i) => (
              <div key={i} className="border border-[var(--tt-border)] rounded-xl p-5 bg-[var(--tt-bg)]">
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold text-[var(--tt-text)]">{p.part}</span>
                  {typeof p.average_self_score === "number" && (
                    <span className="text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)]">
                      Self: {p.average_self_score.toFixed(1)}/5
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-[var(--tt-muted)] leading-relaxed">{p.read}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations + Follow-up */}
      <div className="grid md:grid-cols-2 gap-6">
        <ListCard
          title="Onboarding Recommendations"
          items={analysis.onboarding_recommendations}
          variant="primary"
          testid="recs-card"
        />
        <ListCard
          title="Follow-Up Questions"
          items={analysis.follow_up_questions}
          variant="neutral"
          testid="followup-card"
        />
      </div>
    </div>
  );
}

function ListCard({ title, items, variant = "neutral", testid }) {
  if (!items || items.length === 0) return null;
  const accent = {
    positive: "bg-[#2F3E2F]",
    warning: "bg-[#D98032]",
    primary: "bg-[#A64D2E]",
    neutral: "bg-[#5E5C58]",
  }[variant];

  return (
    <div className="rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] p-7" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-2 h-2 rounded-full ${accent}`} />
        <span className="text-xs uppercase tracking-[0.22em] text-[var(--tt-primary)] font-semibold">
          {title}
        </span>
      </div>
      <ul className="space-y-3">
        {items.map((x, i) => (
          <li key={i} className="text-sm text-[var(--tt-text)] leading-relaxed pl-4 relative">
            <span className="absolute left-0 top-2 w-1 h-1 rounded-full bg-[var(--tt-muted)]" />
            {x}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CulturalCard({ label, subtitle, body }) {
  return (
    <div className="border border-[var(--tt-border)] rounded-xl p-5 bg-[var(--tt-bg)]">
      <div className="font-display text-lg font-semibold text-[var(--tt-text)]">{label}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--tt-muted)] mt-0.5">{subtitle}</div>
      <p className="mt-3 text-sm text-[var(--tt-text)] leading-relaxed">{body}</p>
    </div>
  );
}
